import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import qs from 'qs';
import { Link, useLocation } from 'react-router';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Table, Row, Button, Alert, Divider } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import { useDatevLogs } from 'Datev/hooks/logs';
import { DatevLogEntry } from 'utils/apiClient/services/datev/types';
import { getNextCursor, getPreviousCursor } from '../selectors';
import { formatDate, formatDescription } from '../utils';

const columns: ColumnsType<DatevLogEntry> = [
  {
    title: <FormattedMessage defaultMessage="Date" />,
    dataIndex: 'published',
    key: 'published',
    render: (record) => formatDate(record),
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="Type/Status"
        description="datev logs table"
      />
    ),
    dataIndex: 'verb',
    key: 'verb',
  },
  {
    title: (
      <FormattedMessage
        defaultMessage="Reference"
        description="datev logs table"
      />
    ),
    dataIndex: 'target',
    key: 'target',
  },
  {
    title: <FormattedMessage defaultMessage="Description" />,
    dataIndex: 'action',
    key: 'action',
    render: (record) => formatDescription(record),
  },
];
const Logs = () => {
  const location = useLocation();
  const search = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location]
  );
  const { cursor } = search;
  const logsQuery = useDatevLogs(cursor, search._ === 'logs');

  const nextCursor =
    logsQuery.data !== undefined ? getNextCursor(logsQuery.data) : null;
  const previousCursor =
    logsQuery.data !== undefined ? getPreviousCursor(logsQuery.data!) : null;

  if (logsQuery.error) {
    return (
      <Alert
        message="Server Error"
        type="error"
        description={
          <FormattedMessage defaultMessage="We are currently experiencing issues with the server. Please come back later." />
        }
        showIcon
      />
    );
  }
  return (
    <>
      <Row itemType="flex" justify="end">
        <Link
          to={{
            pathname: location.pathname,
            search: qs.stringify({ ...search, cursor: previousCursor }),
          }}
        >
          <Button disabled={!previousCursor} data-testid="pag-prev">
            <LeftOutlined />
            <FormattedMessage defaultMessage="Go back" />
          </Button>
        </Link>

        <Link
          to={{
            pathname: location.pathname,
            search: qs.stringify({ ...search, cursor: nextCursor }),
          }}
        >
          <Button disabled={!nextCursor} data-testid="pag-next">
            <FormattedMessage defaultMessage="Go next" />
            <RightOutlined />
          </Button>
        </Link>
      </Row>
      <Divider />
      <Table<DatevLogEntry>
        rowKey={(r) => r.id}
        dataSource={logsQuery.data?.results}
        loading={logsQuery.isLoading}
        columns={columns}
        pagination={false}
      />
    </>
  );
};

export default Logs;
